import { useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";

import { useAppDispatch } from "@/redux/store/hooks";
import { highlightText } from "@/helpers/highlight-text";
import { scrollIntoView } from "@/helpers/scroll-into-view";

export default function useInfiniteScroll(
  apiFunction: any,
  apiParams: any,
  content: Array<object>,
  pageNumber: number,
  contentLimiter: number,
  onParamChangeCheck: object,
  contentCount: number,
  toolError?: string,
  highlightClass?: string
) {
  const dispatch = useAppDispatch();

  // hasMore flag decides if we have any more net result
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [previousData, setPreviousData] = useState([]);
  const [updatedParam, setUpdatedParam] = useState({});
  const [hasPageLoadTriggered, setHasPageLoadTriggered] = useState(false);
  const [hasNewContentAdded, setHasNewContentAdded] = useState(false);

  const fetchData = async () => await dispatch(apiFunction(apiParams));

  useMemo(() => {
    setHasNewContentAdded(true);
    if (apiParams?.search) fetchData();
  }, [contentCount]);

  const contentRemain = useMemo(() => {
    const { page } = apiParams;
    const prevPage = page === 1 ? 1 : page - 1;
    // checking if the total number of content and the number of contents loaded in the previous API pages
    const remaining = contentCount - prevPage * contentLimiter;
    return remaining > contentLimiter ? contentLimiter : remaining;
  }, [contentCount, apiParams, contentLimiter]);

  useEffect(() => {
    if (toolError) {
      setLoading(false);
    } else {
      setLoading(true);
      if (!isEqual(onParamChangeCheck, updatedParam)) setPreviousData([]);
      setHasPageLoadTriggered(true);
      fetchData();
    }
  }, [pageNumber, onParamChangeCheck, toolError]);

  useEffect(() => {
    if (isEmpty(content)) {
      if (contentCount <= 0) setData([]);
      setHasMore(false);
      setLoading(false);
      return;
    }

    if (contentRemain < 0) return;
    setData(content);
    setHasMore(content.length >= contentLimiter);

    setData((prev: { [key: string]: string }[]) => {
      let fetchedContent = prev;
      if (
        previousData.length > 0 &&
        isEqual(onParamChangeCheck, updatedParam)
      ) {
        fetchedContent = previousData;
      }

      let cumulatedResult = [...fetchedContent, ...content];

      if (
        !hasPageLoadTriggered &&
        cumulatedResult.length > 2 * content.length
      ) {
        cumulatedResult = sortBy(cumulatedResult, "id").reverse();
      }
      if (hasNewContentAdded) scrollIntoView("ideas-grid");

      const uniqResult = uniqBy(cumulatedResult, "id");

      setPreviousData([...uniqResult, ...previousData]);
      setUpdatedParam({ ...onParamChangeCheck });
      setLoading(false);
      setHasPageLoadTriggered(false);
      setHasNewContentAdded(false);

      return uniqResult;
    });

    if (highlightClass && apiParams.search) {
      setTimeout(() => {
        highlightText(highlightClass, apiParams.search);
      }, 100);
    }
  }, [content]);

  return { loading, data, hasMore, contentRemain };
}
